import { createFileRoute } from '@tanstack/react-router';
import {
  Button,
  Card,
  Group,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { IconBuildingStore, IconCheck } from '@tabler/icons-react';
import { trpc } from '../../lib/trpc';

const Schema = z.object({
  name: z.string().trim().max(200),
  email: z.string().trim().email().or(z.literal('')),
  phone: z.string().trim().max(40),
  timezone: z.string().trim().min(1).max(64),
  defaultLanguage: z.string().trim().min(1).max(8),
  addressLine1: z.string().trim().max(200),
  addressLine2: z.string().trim().max(200),
  city: z.string().trim().max(100),
  state: z.string().trim().max(100),
  postalCode: z.string().trim().max(20),
  country: z.string().trim().max(100),
  website: z.string().trim().max(500),
  logoUrl: z.string().trim().max(1000),
});
type FormValues = z.infer<typeof Schema>;

const TIMEZONES = [
  'UTC',
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Europe/London',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Australia/Sydney',
];

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'ar', label: 'Arabic' },
];

function GeneralSettings() {
  const profile = trpc.tenancy.business.getProfile.useQuery();
  const utils = trpc.useUtils();
  const save = trpc.tenancy.business.setProfile.useMutation({
    onSuccess: () => {
      void utils.tenancy.business.getProfile.invalidate();
      notifications.show({ message: 'Business profile saved', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      timezone: 'UTC',
      defaultLanguage: 'en',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      website: '',
      logoUrl: '',
    },
  });

  useEffect(() => {
    if (profile.data) reset(profile.data);
  }, [profile.data, reset]);

  const onSubmit = handleSubmit((values) => {
    save.mutate(values);
  });

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group gap="sm">
          <IconBuildingStore size={22} />
          <Title order={2}>General settings</Title>
        </Group>
        <Text c="dimmed" size="sm">
          Your business information. Used in templates, the chat composer, and outgoing webhook
          payloads.
        </Text>

        {profile.isPending ? (
          <Skeleton height={400} />
        ) : (
          <form onSubmit={onSubmit}>
            <Stack gap="md">
              <Card>
                <Stack gap="sm">
                  <Title order={5}>Business</Title>
                  <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
                    <TextInput
                      label="Business name"
                      {...register('name')}
                      error={errors.name?.message}
                    />
                    <TextInput
                      label="Contact email"
                      type="email"
                      {...register('email')}
                      error={errors.email?.message}
                    />
                    <TextInput
                      label="Contact phone"
                      {...register('phone')}
                      error={errors.phone?.message}
                    />
                    <TextInput
                      label="Website"
                      placeholder="https://..."
                      {...register('website')}
                      error={errors.website?.message}
                    />
                    <Select
                      label="Timezone"
                      data={TIMEZONES}
                      searchable
                      value={watch('timezone')}
                      onChange={(v) => setValue('timezone', v ?? 'UTC')}
                      error={errors.timezone?.message}
                    />
                    <Select
                      label="Default language"
                      data={LANGUAGES}
                      value={watch('defaultLanguage')}
                      onChange={(v) => setValue('defaultLanguage', v ?? 'en')}
                      error={errors.defaultLanguage?.message}
                    />
                    <TextInput
                      label="Logo URL"
                      placeholder="https://..."
                      {...register('logoUrl')}
                      error={errors.logoUrl?.message}
                      style={{ gridColumn: '1 / -1' }}
                    />
                  </SimpleGrid>
                </Stack>
              </Card>

              <Card>
                <Stack gap="sm">
                  <Title order={5}>Address</Title>
                  <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
                    <TextInput
                      label="Address line 1"
                      {...register('addressLine1')}
                      error={errors.addressLine1?.message}
                    />
                    <TextInput
                      label="Address line 2"
                      {...register('addressLine2')}
                      error={errors.addressLine2?.message}
                    />
                    <TextInput label="City" {...register('city')} error={errors.city?.message} />
                    <TextInput
                      label="State / Region"
                      {...register('state')}
                      error={errors.state?.message}
                    />
                    <TextInput
                      label="Postal code"
                      {...register('postalCode')}
                      error={errors.postalCode?.message}
                    />
                    <TextInput
                      label="Country"
                      {...register('country')}
                      error={errors.country?.message}
                    />
                  </SimpleGrid>
                </Stack>
              </Card>

              <Group>
                <Button
                  type="submit"
                  leftSection={<IconCheck size={16} />}
                  loading={save.isPending}
                >
                  Save changes
                </Button>
              </Group>
            </Stack>
          </form>
        )}
      </Stack>
    </motion.div>
  );
}

export const Route = createFileRoute('/vendor/settings/general')({ component: GeneralSettings });
